/**
 * Module dependencies.
 */

import {
  Image,
  Lightbox,
  PlayVideoButton,
  VideoPlayer,
  VideoPlayerProps
} from 'src/components/core';

import { ReactElement, useState } from 'react';
import { color, media } from '@untile/react-components';
import { ifNotProp } from 'styled-tools';
import styled, { css } from 'styled-components';

/**
 * Export `GalleryItem` type.
 */

export type GalleryItem = {
  coverImage?: string | null;
  label?: string | null;
  type: 'image' | 'video';
  url: string;
};

/**
 * `Props` type.
 */

type Props = GalleryItem & {
  hasSiblings?: boolean;
  isCarouselSlide?: boolean;
  isMobileDevice: boolean;
};

/**
 * `ImageWrapper` styled component.
 */

const ImageWrapper = styled.div`
  cursor: pointer;
  height: 0;
  overflow: hidden;
  padding-bottom: 61%;
  position: relative;

  ${media.min('md')`
    padding-bottom: 56%;
  `}
`;

/**
 * `VideoWrapper` styled component.
 */

const VideoWrapper = styled.div<{ hasSiblings?: boolean }>`
  ${ifNotProp(
    'hasSiblings',
    css`
      padding-bottom: 61%;
    `
  )}
`;

/**
 * `StyledVideoPlayer` styled component.
 */

const StyledVideoPlayer = styled(VideoPlayer)<
  VideoPlayerProps & {
    resetSize: boolean;
  }
>`
  ${ifNotProp(
    'resetSize',
    css`
      padding-top: unset;
    `
  )}
`;

/**
 * `MobileImageWrapper` styled component.
 */

const MobileImageWrapper = styled.div`
  bottom: 0;
  left: 0;
  overflow: hidden;
  position: absolute;
  right: 0;
  top: 0;
`;

/**
 * `PlayVideoButtonWrapper` styled component.
 */

const PlayVideoButtonWrapper = styled.div`
  button {
    background-color: ${color.transparentize('grey200', 0.4)};

    &::before {
      background-color: ${color.transparentize('grey200', 0.2)};
    }
  }
`;

/**
 * `ImageVideoSlide` component.
 */

const ImageVideoSlide = (props: Props): ReactElement => {
  const {
    coverImage,
    hasSiblings,
    isCarouselSlide = true,
    isMobileDevice,
    label,
    type,
    url
  } = props;

  const [isOpenLightbox, setOpenLightbox] = useState<boolean>(false);

  return (
    <>
      {type === 'image' && (
        <ImageWrapper>
          <Image
            alt={label}
            fill
            src={url}
            style={{ objectFit: 'cover' }}
          />
        </ImageWrapper>
      )}

      {type === 'video' &&
        (isMobileDevice ? (
          <VideoWrapper hasSiblings={hasSiblings}>
            <MobileImageWrapper>
              <Image
                alt={coverImage}
                fill
                src={coverImage}
                style={{ objectFit: 'cover' }}
              />
            </MobileImageWrapper>

            <PlayVideoButtonWrapper onClick={() => setOpenLightbox(true)}>
              <PlayVideoButton />
            </PlayVideoButtonWrapper>

            <Lightbox
              isOpen={isOpenLightbox}
              onClose={() => setOpenLightbox(false)}
              videoUrl={url}
            />
          </VideoWrapper>
        ) : (
          <StyledVideoPlayer
            image={coverImage}
            playing
            resetSize={!isCarouselSlide}
            url={url}
          />
        ))}
    </>
  );
};

/**
 * Export `ImageVideoSlide` component.
 */

export default ImageVideoSlide;
